import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import thankYouImage from "assets/expressReport/thankYou/thankyou.png";
import {
  AlertTriangle,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
} from "lucide-react";

import { CreditReportMetadata } from "js/components/ExpressReport/types";
import { Button } from "js/components/ui/button";

export interface ThankYouProps {
  metadata: CreditReportMetadata | null;
  error_type: string | null;
}

const ThankYou: React.FC<ThankYouProps> = ({ metadata, error_type }) => {
  const navigate = useNavigate();

  const { userToken } = useSelector((state) => state.entities.auth);

  const handleClick = () => {
    if (metadata && metadata?.return_url)
      window.location.assign(metadata.return_url);
    else if (userToken) {
      navigate("/");
    } else {
      window.location.assign(
        "https://api.whatsapp.com/send/?phone=5213333324037&text=%C2%A1Hola!%20Me%20interesa%20el%20servicio%20de%20Multibur%C3%B3%20Express.",
      );
    }
  };

  // Error state (blocked)
  if (error_type === "blocked") {
    return (
      <div
        id="thank-you"
        className="flex flex-col items-center justify-center px-4 py-4"
      >
        <div className="space-y-4 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-red-600 sm:text-2xl">
            Aviso Importante: Tu historial crediticio está bloqueado
          </h2>
          <p className="mx-auto max-w-lg text-sm text-red-600">
            Tu historial crediticio se encuentra actualmente bloqueado para
            consultas de terceros. Para poder continuar con la consulta, es
            necesario que te pongas en contacto con Buró de Crédito y solicites
            que se habilite la consulta. Una vez que se haya habilitado, deberás
            notificarnos para que podamos concluir el proceso.
          </p>
        </div>
      </div>
    );
  }

  // Error state (ambiguous)
  if (error_type === "ambiguous") {
    return (
      <div
        id="thank-you"
        className="flex flex-col items-center justify-center px-4 py-4"
      >
        <div className="space-y-4 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-6 w-6 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-amber-600 sm:text-2xl">
            Aviso: Se encontraron coincidencias ambiguas
          </h2>
          <p className="mx-auto max-w-lg text-sm text-amber-600">
            Se encontraron múltiples coincidencias con los datos proporcionados
            en Buró de Crédito. Por favor, contacta a tu asesor para que pueda
            ayudarte a resolver esta situación.
          </p>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div
      id="thank-you"
      className="flex flex-col items-center justify-center px-4 py-4"
    >
      {/* Image */}
      <div className="mb-4 w-full max-w-xs">
        <img
          src={thankYouImage}
          alt="Thank You"
          className="h-auto w-full rounded-xl"
        />
      </div>

      {/* Text */}
      <h2 className="mb-4 max-w-md text-center text-xl font-semibold text-gray-900">
        Tú también puedes crecer tu negocio perfilando más y mejores clientes
      </h2>

      {/* Button */}
      <Button
        onClick={handleClick}
        className="mb-6 bg-[#0060ff] text-white hover:bg-[#0052d4]"
      >
        {userToken ? "Regresar" : "Me interesa"}
      </Button>

      {/* Social Media Section */}
      <div className="space-y-3 text-center">
        <p className="text-sm text-gray-500">
          Síguenos en nuestras redes sociales
        </p>
        <div className="flex items-center justify-center gap-4">
          <a
            href="https://www.facebook.com/multiburo.com.mx"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 transition-colors hover:text-[#0060ff]"
            aria-label="Facebook"
          >
            <Facebook className="h-5 w-5" />
          </a>
          <a
            href="https://www.instagram.com/multiburomx/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 transition-colors hover:text-[#0060ff]"
            aria-label="Instagram"
          >
            <Instagram className="h-5 w-5" />
          </a>
          <a
            href="https://www.linkedin.com/company/multiburomx"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 transition-colors hover:text-[#0060ff]"
            aria-label="LinkedIn"
          >
            <Linkedin className="h-5 w-5" />
          </a>
          <a
            href="https://www.youtube.com/channel/UCY21TvJcDDLkCC_hTMpW0pQ"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 transition-colors hover:text-[#0060ff]"
            aria-label="YouTube"
          >
            <Youtube className="h-5 w-5" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
